import { useMemo } from 'react'

import { useFlags } from 'launchdarkly-react-client-sdk'
import getConfig from 'next/config'

import { useLocalStorageBackedState } from '~/hooks/useLocalStorageBackedState'

const { publicRuntimeConfig } = getConfig()

interface Flags {
  [key: string]: any
}

const getFeatureFileFlags = () => {
  const flagValues = publicRuntimeConfig.features?.flagValues ?? {}
  const featureFlags = publicRuntimeConfig.features?.flags ?? {}
  const flags = Object.keys(featureFlags).reduce((sum: Flags, key: string) => {
    sum[key] = featureFlags[key]?.on
    return sum
  }, {})
  return { ...flagValues, ...flags }
}

// this hook merges the feature flags from the feature-flags.yml file and LaunchDarkly
export const useFeatureFlags = () => {
  const featureFileFlags = getFeatureFileFlags()
  const launchDarklyFlags = useFlags()
  return { ...featureFileFlags, ...launchDarklyFlags }
}

const gameRankStorageKey = 'feature_game_rank'
const insightsStorageKey = 'feature_insights'
const totInsightsStorageKey = 'feature_tot_insights'
const disillusionInsightsStorageKey = 'feature_disillusion_insights'
const ebbAndFlowInsightsStorageKey = 'feature_ebb_and_flow_insights'
const lostInMigrationStorageKey = 'feature_lost_in_migration_insights'
const rainDropsInsightsStorageKey = 'feature_rain_drops_insights'

// this hook stores the feature flags in localStorage, which can be changed at runtime
export const useLocalFeatures = () => {
  const [gameRankValue, setGameRankValue] = useLocalStorageBackedState<boolean>(gameRankStorageKey, false)
  const [insights, setInsights] = useLocalStorageBackedState<boolean>(insightsStorageKey, false)
  const [totInsights, setTotInsights] = useLocalStorageBackedState<boolean>(totInsightsStorageKey, false)
  const [disillusionInsights, setDisillusionInsights] = useLocalStorageBackedState<boolean>(
    disillusionInsightsStorageKey,
    false,
  )
  const [ebbAndFlowInsights, setEbbAndFlowInsights] = useLocalStorageBackedState<boolean>(
    ebbAndFlowInsightsStorageKey,
    false,
  )
  const [lostInMigration, setLostInMigration] = useLocalStorageBackedState<boolean>(lostInMigrationStorageKey, false)
  const [rainDropsInsights, setRainDropsInsights] = useLocalStorageBackedState<boolean>(
    rainDropsInsightsStorageKey,
    false,
  )

  const featureFlags = useMemo(() => {
    return {
      gameRank: {
        title: 'Game Rank & Progress',
        storageKey: gameRankStorageKey,
        active: gameRankValue,
        setActive: setGameRankValue,
      },
      insights: {
        title: 'Insights',
        storageKey: insightsStorageKey,
        active: insights,
        setActive: setInsights,
      },
      totInsights: {
        title: 'Tot Insights',
        storageKey: totInsightsStorageKey,
        active: totInsights,
        setActive: setTotInsights,
      },
      disillusionInsights: {
        title: 'Disillusion Insights',
        storageKey: disillusionInsightsStorageKey,
        active: disillusionInsights,
        setActive: setDisillusionInsights,
      },
      ebbAndFlowInsights: {
        title: 'Ebb And Flow Insights',
        storageKey: ebbAndFlowInsightsStorageKey,
        active: ebbAndFlowInsights,
        setActive: setEbbAndFlowInsights,
      },
      lostInMigrationInsights: {
        title: 'Lost In Migration Insights',
        storageKey: lostInMigrationStorageKey,
        active: lostInMigration,
        setActive: setLostInMigration,
      },
      rainDropsInsights: {
        title: 'Rain Drops Insights',
        storageKey: rainDropsInsightsStorageKey,
        active: rainDropsInsights,
        setActive: setRainDropsInsights,
      },
    } as const
  }, [
    disillusionInsights,
    ebbAndFlowInsights,
    gameRankValue,
    insights,
    lostInMigration,
    rainDropsInsights,
    setDisillusionInsights,
    setEbbAndFlowInsights,
    setGameRankValue,
    setInsights,
    setLostInMigration,
    setRainDropsInsights,
    setTotInsights,
    totInsights,
  ])

  return { featureFlags }
}
